import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Image,
  Video,
  FileText,
  Upload,
  Search,
  Trash2,
  Loader2,
  X,
  Calendar,
  Link,
  File,
  Play,
  Eye,
  Download,
  Plus
} from 'lucide-react';
import { Card, CardContent } from '@easyflow/ui-frontend/components/ui/card';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@easyflow/ui-frontend/components/ui/dialog';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { ScrollArea } from '@easyflow/ui-frontend/components/ui/scroll-area';
import { apiClient } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { getErrorMessage } from '../../lib/utils';


interface MediaItem {
  id: string;
  friendly_name: string;
  media_type: 'image' | 'video' | 'document';
  media_url: string;
  mime_type?: string;
  file_size?: number;
  created_at: string;
}

interface MediaLibraryProps {
  onSelect?: (media: MediaItem) => void;
  selectionMode?: boolean;
}

type FilterType = 'all' | 'image' | 'video' | 'document';

const FILTERS: { key: FilterType; label: string; icon: React.ReactNode }[] = [
  { key: 'all', label: 'Tutti', icon: <File className="h-4 w-4" /> },
  { key: 'image', label: 'Immagini', icon: <Image className="h-4 w-4" /> },
  { key: 'video', label: 'Video', icon: <Video className="h-4 w-4" /> },
  { key: 'document', label: 'Documenti', icon: <FileText className="h-4 w-4" /> }
];

function formatFileSize(bytes?: number): string {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}

function getMediaIcon(type: string) {
  switch (type) {
    case 'image':
      return <Image className="h-8 w-8 text-blue-400" />;
    case 'video':
      return <Video className="h-8 w-8 text-purple-400" />;
    default:
      return <FileText className="h-8 w-8 text-orange-400" />;
  }
}

function getTypeBadgeVariant(type: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (type) {
    case 'image':
      return 'default';
    case 'video':
      return 'secondary';
    default:
      return 'outline';
  }
}

function getTypeLabel(type: string): string {
  switch (type) {
    case 'image':
      return 'Immagine';
    case 'video':
      return 'Video';
    default:
      return 'Documento';
  }
}

export default function MediaLibrary({ onSelect, selectionMode = false }: MediaLibraryProps) {
  const { toast } = useToast();
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterType>('all');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const limit = 20;

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    friendlyName: '',
    mediaType: 'image' as 'image' | 'video' | 'document',
    mediaUrl: '',
    mimeType: '',
    fileSize: ''
  });

  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadMedia = useCallback(async (reset = false) => {
    try {
      setLoading(true);
      const currentOffset = reset ? 0 : offset;
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('type', filter);
      if (search) params.append('search', search);
      params.append('limit', limit.toString());
      params.append('offset', currentOffset.toString());

      const res = await apiClient.get<{ data: MediaItem[]; total?: number }>(`/messaging/media?${params.toString()}`);
      const items = res.success ? res.data?.data : undefined;
      if (items) {
        if (reset) {
          setMedia(items);
          setOffset(limit);
        } else {
          setMedia(prev => [...prev, ...items]);
          setOffset(currentOffset + limit);
        }
        setHasMore(items.length === limit);
      }
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error) || 'Impossibile caricare i media', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [filter, search, offset, toast]);

  useEffect(() => {
    loadMedia(true);
  }, [filter, search, loadMedia]);

  const handleUpload = async () => {
    if (!uploadForm.friendlyName || !uploadForm.mediaUrl) {
      toast({ title: 'Errore', description: 'Nome e URL sono obbligatori', variant: 'destructive' });
      return;
    }

    setUploading(true);
    try {
      const payload: {
        friendlyName: string;
        mediaType: string;
        mediaUrl: string;
        mimeType?: string;
        fileSize?: number;
      } = {
        friendlyName: uploadForm.friendlyName,
        mediaType: uploadForm.mediaType,
        mediaUrl: uploadForm.mediaUrl
      };
      if (uploadForm.mimeType) payload.mimeType = uploadForm.mimeType;
      if (uploadForm.fileSize) payload.fileSize = parseInt(uploadForm.fileSize);

      const res = await apiClient.post<MediaItem>('/messaging/media', payload);
      if (res.success) {
        toast({ title: 'Media caricato', description: 'Il media è stato aggiunto alla libreria' });
        setUploadOpen(false);
        setUploadForm({ friendlyName: '', mediaType: 'image', mediaUrl: '', mimeType: '', fileSize: '' });
        loadMedia(true);
      } else {
        toast({ title: 'Errore', description: res.error, variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeleting(id);
    try {
      const res = await apiClient.delete(`/messaging/media/${id}`);
      if (res.success) {
        toast({ title: 'Media eliminato' });
        setMedia(prev => prev.filter(m => m.id !== id));
        if (selectedMedia?.id === id) {
          setPreviewOpen(false);
          setSelectedMedia(null);
        }
      } else {
        toast({ title: 'Errore', description: res.error, variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setDeleting(null);
    }
  };

  const handleCardClick = (item: MediaItem) => {
    if (selectionMode && onSelect) {
      onSelect(item);
    } else {
      setSelectedMedia(item);
      setPreviewOpen(true);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const url = e.dataTransfer.getData('text/uri-list') || e.dataTransfer.getData('text/plain');
    if (url && url.startsWith('http')) {
      setUploadForm(prev => ({ ...prev, mediaUrl: url }));
      const extension = url.split('.').pop()?.toLowerCase();
      if (extension) {
        if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)) {
          setUploadForm(prev => ({ ...prev, mediaType: 'image' }));
        } else if (['mp4', 'webm', 'mov', 'avi'].includes(extension)) {
          setUploadForm(prev => ({ ...prev, mediaType: 'video' }));
        } else {
          setUploadForm(prev => ({ ...prev, mediaType: 'document' }));
        }
      }
    }
  };

  const renderThumbnail = (item: MediaItem) => {
    if (item.media_type === 'image') {
      return (
        <img
          src={item.media_url}
          alt={item.friendly_name}
          className="w-full h-32 object-cover rounded-t-lg"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
            (e.target as HTMLImageElement).parentElement!.classList.add('flex', 'items-center', 'justify-center', 'bg-slate-800/50');
            const icon = document.createElement('div');
            icon.innerHTML = '<svg class="h-12 w-12 text-slate-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>';
            (e.target as HTMLImageElement).parentElement!.appendChild(icon);
          }}
        />
      );
    }
    if (item.media_type === 'video') {
      return (
        <div className="w-full h-32 bg-slate-800/50 rounded-t-lg flex items-center justify-center relative overflow-hidden">
          <video
            src={item.media_url}
            className="w-full h-full object-cover absolute inset-0"
            muted
            onLoadedData={(e) => (e.target as HTMLVideoElement).currentTime = 1}
          />
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
            <Play className="h-10 w-10 text-white/80" />
          </div>
        </div>
      );
    }
    return (
      <div className="w-full h-32 bg-slate-800/50 rounded-t-lg flex items-center justify-center">
        <FileText className="h-12 w-12 text-slate-500" />
      </div>
    );
  };

  const renderPreview = () => {
    if (!selectedMedia) return null;
    
    if (selectedMedia.media_type === 'image') {
      return (
        <img
          src={selectedMedia.media_url}
          alt={selectedMedia.friendly_name}
          className="max-w-full max-h-[60vh] object-contain rounded-lg mx-auto"
        />
      );
    }
    if (selectedMedia.media_type === 'video') {
      return (
        <video
          src={selectedMedia.media_url}
          controls
          className="max-w-full max-h-[60vh] rounded-lg mx-auto"
        />
      );
    }
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <FileText className="h-20 w-20 text-slate-400" />
        <p className="text-slate-400">Anteprima non disponibile per questo tipo di file</p>
        <a
          href={selectedMedia.media_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-400 hover:underline flex items-center gap-2"
        >
          <Download className="h-4 w-4" />
          Scarica documento
        </a>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex flex-wrap gap-2">
          {FILTERS.map(f => (
            <Button
              key={f.key}
              variant={filter === f.key ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f.key)}
              className="gap-2"
            >
              {f.icon}
              {f.label}
            </Button>
          ))}
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Cerca media..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button onClick={() => setUploadOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Carica
          </Button>
        </div>
      </div>

      {loading && media.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : media.length === 0 ? (
        <Card variant="glass" className="p-12 text-center">
          <div className="flex flex-col items-center space-y-4">
            <Image className="h-16 w-16 text-slate-500" />
            <h3 className="text-lg font-semibold">Nessun media trovato</h3>
            <p className="text-muted-foreground max-w-md">
              {search || filter !== 'all'
                ? 'Prova a modificare i filtri o la ricerca'
                : 'Inizia caricando il tuo primo media'}
            </p>
            {!search && filter === 'all' && (
              <Button onClick={() => setUploadOpen(true)} className="gap-2 mt-2">
                <Upload className="h-4 w-4" />
                Carica media
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {media.map(item => (
              <Card
                key={item.id}
                variant="glass"
                className="overflow-hidden cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all group"
                onClick={() => handleCardClick(item)}
              >
                <div className="relative">
                  {renderThumbnail(item)}
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      variant="destructive"
                      size="icon"
                      className="h-8 w-8"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(item.id);
                      }}
                      disabled={deleting === item.id}
                    >
                      {deleting === item.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
                <CardContent className="p-3 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-medium text-sm truncate flex-1" title={item.friendly_name}>
                      {item.friendly_name}
                    </p>
                    <Badge variant={getTypeBadgeVariant(item.media_type)} className="text-xs shrink-0">
                      {getTypeLabel(item.media_type)}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    {formatDate(item.created_at)}
                    {item.file_size && (
                      <>
                        <span>•</span>
                        <span>{formatFileSize(item.file_size)}</span>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {hasMore && (
            <div className="flex justify-center pt-4">
              <Button
                variant="outline"
                onClick={() => loadMedia(false)}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Caricamento...
                  </>
                ) : (
                  'Carica altri'
                )}
              </Button>
            </div>
          )}
        </>
      )}

      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Carica Media</DialogTitle>
            <DialogDescription>
              Aggiungi un nuovo media alla libreria inserendo l'URL
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragOver ? 'border-primary bg-primary/10' : 'border-muted-foreground/25'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Trascina un link qui oppure inserisci l'URL sotto
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="mediaUrl">URL Media *</Label>
              <div className="relative">
                <Link className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="mediaUrl"
                  placeholder="https://esempio.com/immagine.jpg"
                  value={uploadForm.mediaUrl}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, mediaUrl: e.target.value }))}
                  className="pl-9"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="friendlyName">Nome *</Label>
              <Input
                id="friendlyName"
                placeholder="Nome del media"
                value={uploadForm.friendlyName}
                onChange={(e) => setUploadForm(prev => ({ ...prev, friendlyName: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label>Tipo</Label>
              <div className="flex gap-2">
                {(['image', 'video', 'document'] as const).map(type => (
                  <Button
                    key={type}
                    type="button"
                    variant={uploadForm.mediaType === type ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setUploadForm(prev => ({ ...prev, mediaType: type }))}
                    className="gap-2 flex-1"
                  >
                    {type === 'image' && <Image className="h-4 w-4" />}
                    {type === 'video' && <Video className="h-4 w-4" />}
                    {type === 'document' && <FileText className="h-4 w-4" />}
                    {getTypeLabel(type)}
                  </Button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="mimeType">MIME Type (opzionale)</Label>
                <Input
                  id="mimeType"
                  placeholder="image/jpeg"
                  value={uploadForm.mimeType}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, mimeType: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="fileSize">Dimensione (bytes, opzionale)</Label>
                <Input
                  id="fileSize"
                  type="number"
                  placeholder="1024"
                  value={uploadForm.fileSize}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, fileSize: e.target.value }))}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleUpload} disabled={uploading}>
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Caricamento...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Carica
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedMedia && getMediaIcon(selectedMedia.media_type)}
              {selectedMedia?.friendly_name}
            </DialogTitle>
            <DialogDescription className="flex items-center gap-4 flex-wrap">
              {selectedMedia && (
                <>
                  <Badge variant={getTypeBadgeVariant(selectedMedia.media_type)}>
                    {getTypeLabel(selectedMedia.media_type)}
                  </Badge>
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {formatDate(selectedMedia.created_at)}
                  </span>
                  {selectedMedia.file_size && (
                    <span>{formatFileSize(selectedMedia.file_size)}</span>
                  )}
                  {selectedMedia.mime_type && (
                    <span className="text-xs font-mono">{selectedMedia.mime_type}</span>
                  )}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {renderPreview()}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <div className="flex-1 flex items-center gap-2 overflow-hidden">
              <Link className="h-4 w-4 shrink-0 text-muted-foreground" />
              <code className="text-xs bg-slate-800/50 px-2 py-1 rounded truncate flex-1">
                {selectedMedia?.media_url}
              </code>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  if (selectedMedia) {
                    navigator.clipboard.writeText(selectedMedia.media_url);
                    toast({ title: 'URL copiato' });
                  }
                }}
              >
                Copia URL
              </Button>
              <a
                href={selectedMedia?.media_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="outline" className="gap-2">
                  <Eye className="h-4 w-4" />
                  Apri
                </Button>
              </a>
              <Button
                variant="destructive"
                onClick={() => selectedMedia && handleDelete(selectedMedia.id)}
                disabled={!!deleting}
              >
                {deleting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
